import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/motion";
import { Faq } from "../components/Faq";
import { Breadcrumbs } from "../components/Breadcrumbs";
import NotFound from "./NotFound";
import { PLANS, money, url, faqSchema, breadcrumbSchema } from "../data/site";
import { guidesForState } from "../data/blog";
import {
  STATES, REGIONS, NO_WAGE_TAX, LOCAL_TAX, PAID_LEAVE,
  slugToAbbr, stateSlug, regionOf, joinNames, stateObligations, stateIntro, stateFaqs,
} from "../data/states";

export default function StatePage() {
  const { slug } = useParams();
  const abbr = slugToAbbr[slug];
  if (!abbr) return <NotFound />;

  const [name, cities] = STATES[abbr];
  const region = regionOf(abbr);
  const path = `/payroll-services/${slug}`;
  const ess = PLANS.essentials;
  const qas = stateFaqs(abbr);
  const trail = [["Home", "/"], ["States", "/payroll-services"], [name, path]];

  const others = REGIONS[region].filter((a) => a !== abbr).sort((a, b) => STATES[a][0].localeCompare(STATES[b][0]));
  const facts = [
    ["State income tax on wages", NO_WAGE_TAX.has(abbr) ? "No" : "Yes"],
    ["Local payroll taxes", LOCAL_TAX.has(abbr) ? "In some areas" : "Not typical"],
    ["Payroll-funded leave program", PAID_LEAVE.has(abbr) ? "Yes" : "No"],
    ["ABSLpay per-state fee", "None"],
  ];

  const serviceSchema = {
    "@context": "https://schema.org", "@type": "Service",
    name: `Payroll services in ${name}`, serviceType: "Payroll services",
    provider: { "@id": url("/#org") },
    areaServed: { "@type": abbr === "DC" ? "AdministrativeArea" : "State", name },
    offers: {
      "@type": "Offer", priceCurrency: "USD", price: ess.base, url: url("/pricing"),
      description: `${money(ess.base)}/month + ${money(ess.per)} per person paid`,
    },
  };

  let desc = `Full-service ${name} payroll: direct deposit, tax filing, W-2s and new-hire reporting. CPA-reviewed, from ${money(ess.base)}/mo + ${money(ess.per)}/person.`;
  const near = ` Serving ${joinNames(cities.slice(0, 2))}.`;
  if ((desc + near).length <= 160) desc += near;

  return (
    <>
      <Seo
        title={`${name} Payroll Services from $39/mo | ABSLpay`}
        description={desc}
        path={path}
        jsonLd={[serviceSchema, faqSchema(qas), breadcrumbSchema(trail)]}
      />
      <Breadcrumbs trail={trail} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#ECFDF5_0%,transparent_50%)]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 lg:pt-16">
          <MaskedLines
            lines={[`Payroll services for`, `${name} employers`]}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900"
          />
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-600">
            ABSLpay pays your team and files every federal and {name} payroll return for you, with a CPA available when
            questions come up. Serving businesses in {joinNames(cities)} and across the state.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to={`/contact?state=${abbr}`}
              data-testid="state-get-quote-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white font-bold px-7 py-3.5 hover:bg-emerald-700 transition-colors"
            >
              Get a {name} payroll quote <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/pricing" data-testid="state-see-pricing-btn" className="inline-flex items-center rounded-full border border-slate-300 bg-white font-bold px-7 py-3.5 text-slate-800 hover:border-slate-900 transition-colors">
              See pricing
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">What {name} payroll involves</h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">{stateIntro(abbr)}</p>
            </Reveal>
            <ul className="mt-8 space-y-5">
              {stateObligations(abbr).map(([t, d], i) => (
                <Reveal key={t} delay={i * 0.06}>
                  <li className="flex gap-4">
                    <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                    <div>
                      <strong className="block font-display text-base font-bold text-slate-900">{t}</strong>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{d}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
            <p className="mt-8 text-sm text-slate-400">
              State rules change often. We monitor rate notices and law changes for every state where our clients pay employees.
            </p>
          </div>
          <Reveal>
            <aside className="rounded-2xl border border-slate-200 bg-[#FAFAFA] p-7 lg:sticky lg:top-28" aria-label={`${name} payroll at a glance`} data-testid="state-facts-card">
              <h3 className="font-display text-lg font-bold text-slate-900">{name} at a glance</h3>
              <dl className="mt-5 space-y-3">
                {facts.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 text-sm border-b border-slate-200 pb-3 last:border-0">
                    <dt className="text-slate-500">{k}</dt>
                    <dd className="font-bold text-slate-900 text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-mono-num text-4xl font-bold text-slate-900">{money(ess.base)}</span>
                <span className="text-slate-500">/month</span>
              </div>
              <p className="text-sm text-slate-600">+ {money(ess.per)} per person paid</p>
              <Link
                to={`/contact?state=${abbr}`}
                data-testid="state-aside-quote-btn"
                className="mt-6 flex justify-center rounded-full bg-emerald-600 text-white font-bold px-5 py-3 hover:bg-emerald-700 transition-colors"
              >
                Get a free quote
              </Link>
            </aside>
          </Reveal>
        </div>
      </section>

      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <Reveal><h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{name} payroll questions</h2></Reveal>
          <div className="mt-10"><Faq qas={qas} testPrefix="state-faq" /></div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">From the CPA's desk</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Payroll guides for {name} employers
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="state-related-guides">
            {guidesForState(abbr).map((g, i) => (
              <Reveal key={g.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${g.slug}`}
                  data-testid={`state-guide-card-${g.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:border-emerald-400 hover:-translate-y-1"
                >
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">{g.category} &middot; {g.readTime}</span>
                  <h3 className="mt-3 font-display text-base font-bold leading-snug text-slate-900 group-hover:text-emerald-800 transition-colors">{g.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{g.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700">
                    Read the guide <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Payroll elsewhere in the {region}</h2>
            <p className="mt-4 max-w-2xl text-base text-slate-600">
              Employees in more than one state? Every plan includes multi-state payroll at no extra cost.
            </p>
            <ul className="mt-8 flex flex-wrap gap-2.5">
              {others.map((a) => (
                <li key={a}>
                  <Link
                    to={`/payroll-services/${stateSlug(a)}`}
                    className="inline-flex rounded-full border border-slate-200 bg-[#FAFAFA] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-emerald-500 hover:text-emerald-800"
                  >
                    {STATES[a][0]}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-8">
              <Link to="/payroll-services" className="font-semibold text-emerald-700 hover:underline">View all 50 states and DC</Link>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
